import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { adminService } from '../../../services/adminService';

const FinancialOversight = () => {
  const [transactions, setTransactions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState({ totalRevenue: 0, totalFees: 0, totalRefunds: 0, monthlyRevenue: 0, transactionCount: 0 });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refundModal, setRefundModal] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [processingRefund, setProcessingRefund] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [txData, payoutData, summaryData] = await Promise.all([
        adminService?.getAllTransactions(),
        adminService?.getPayoutHistory(),
        adminService?.getFinancialSummary()
      ]);
      setTransactions(txData);
      setPayouts(payoutData);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error loading financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'succeeded': return { label: 'Succeeded', color: 'bg-success/10 text-success', icon: 'CheckCircle' };
      case 'completed': return { label: 'Completed', color: 'bg-success/10 text-success', icon: 'CheckCircle' };
      case 'pending': return { label: 'Pending', color: 'bg-warning/10 text-warning', icon: 'Clock' };
      case 'processing': return { label: 'Processing', color: 'bg-primary/10 text-primary', icon: 'Loader' };
      case 'failed': return { label: 'Failed', color: 'bg-error/10 text-error', icon: 'XCircle' };
      case 'refunded': return { label: 'Refunded', color: 'bg-orange-500/10 text-orange-500', icon: 'RotateCcw' };
      default: return { label: status || 'Unknown', color: 'bg-muted text-muted-foreground', icon: 'HelpCircle' };
    }
  };

  const handleProcessRefund = async () => {
    if (!refundModal || !refundAmount) return;
    setProcessingRefund(true);
    try {
      await adminService?.processRefund(refundModal?.id, parseFloat(refundAmount), refundReason);
      setRefundModal(null);
      setRefundAmount('');
      setRefundReason('');
      await loadData();
      showToast('Refund processed successfully');
    } catch (error) {
      showToast('Failed to process refund: ' + (error?.message || ''), 'error');
    } finally {
      setProcessingRefund(false);
    }
  };

  const filteredTransactions = transactions?.filter(t => {
    if (filter === 'all') return true;
    return t?.paymentStatus === filter || t?.status === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mr-3" />
        <span className="font-body text-muted-foreground">Loading financial data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg font-body text-sm font-medium ${
          toast?.type === 'error' ? 'bg-error text-white' : 'bg-success text-white'
        }`}>
          {toast?.message}
        </div>
      )}
      {/* Financial Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="DollarSign" size={20} strokeWidth={2} className="text-primary" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">${summary?.totalRevenue?.toLocaleString()}</p>
          <p className="font-caption text-sm text-muted-foreground">Total Revenue</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="TrendingUp" size={20} strokeWidth={2} className="text-success" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">${summary?.totalFees?.toLocaleString()}</p>
          <p className="font-caption text-sm text-muted-foreground">Platform Fees</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="Calendar" size={20} strokeWidth={2} className="text-warning" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">${summary?.monthlyRevenue?.toLocaleString()}</p>
          <p className="font-caption text-sm text-muted-foreground">This Month</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-orange-500/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="RotateCcw" size={20} strokeWidth={2} className="text-orange-500" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">${summary?.totalRefunds?.toLocaleString()}</p>
          <p className="font-caption text-sm text-muted-foreground">Total Refunds</p>
        </div>
      </div>
      {/* Filter + Refresh */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center space-x-2 flex-wrap gap-2">
          {['all', 'succeeded', 'pending', 'failed']?.map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-md font-body text-sm font-medium transition-smooth capitalize ${
                filter === status ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <Icon name="RefreshCw" size={14} strokeWidth={2} className="mr-1" />
          Refresh
        </Button>
      </div>
      {/* Recent Transactions */}
      <div className="bg-card rounded-lg shadow-elevation-2">
        <div className="p-6 border-b border-border">
          <h3 className="font-heading text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Receipt" size={20} strokeWidth={2} className="text-primary" />
            <span>Transactions ({filteredTransactions?.length})</span>
          </h3>
        </div>
        <div className="divide-y divide-border">
          {filteredTransactions?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="Inbox" size={48} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-3" />
              <p className="font-body text-muted-foreground">No transactions found</p>
            </div>
          ) : (
            filteredTransactions?.map((transaction) => {
              const statusConfig = getStatusConfig(transaction?.paymentStatus);
              return (
                <div key={transaction?.id} className="p-5 hover:bg-muted/50 transition-smooth">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <h4 className="font-body font-semibold text-foreground text-sm">
                          #{transaction?.bookingReference || transaction?.id?.slice(0, 8)}
                        </h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.color} flex items-center space-x-1`}>
                          <Icon name={statusConfig?.icon} size={12} strokeWidth={2} />
                          <span>{statusConfig?.label}</span>
                        </span>
                        {transaction?.refundStatus === 'processed' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-500/10 text-orange-500">
                            Refunded ${transaction?.refundAmount}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center flex-wrap gap-3 text-xs text-muted-foreground mb-2">
                        <span className="flex items-center space-x-1">
                          <Icon name="User" size={12} strokeWidth={2} />
                          <span>Renter: {transaction?.renter?.fullName || transaction?.renter?.full_name || 'N/A'}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Icon name="User" size={12} strokeWidth={2} />
                          <span>Owner: {transaction?.owner?.fullName || transaction?.owner?.full_name || 'N/A'}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Icon name="Package" size={12} strokeWidth={2} />
                          <span>{transaction?.trailer?.title || 'Trailer'}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Icon name="Calendar" size={12} strokeWidth={2} />
                          <span>{new Date(transaction?.createdAt)?.toLocaleDateString()}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="font-body font-semibold text-foreground">
                          Total: ${parseFloat(transaction?.totalAmount || 0)?.toFixed(2)}
                        </span>
                        {transaction?.platformFee && (
                          <span className="font-body text-muted-foreground">
                            Fee: ${parseFloat(transaction?.platformFee || 0)?.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {transaction?.paymentStatus === 'succeeded' && transaction?.refundStatus !== 'processed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRefundModal(transaction)}
                        >
                          <Icon name="RotateCcw" size={14} strokeWidth={2} />
                          <span className="ml-1">Refund</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      {/* Payout History */}
      <div className="bg-card rounded-lg shadow-elevation-2">
        <div className="p-6 border-b border-border">
          <h3 className="font-heading text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Wallet" size={20} strokeWidth={2} className="text-primary" />
            <span>Payout History ({payouts?.length})</span>
          </h3>
        </div>
        <div className="divide-y divide-border">
          {payouts?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="Inbox" size={48} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-3" />
              <p className="font-body text-muted-foreground">No payout records found</p>
            </div>
          ) : (
            payouts?.map((payout) => {
              const statusConfig = getStatusConfig(payout?.payoutStatus || payout?.status);
              return (
                <div key={payout?.id} className="p-5 hover:bg-muted/50 transition-smooth">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <h4 className="font-body font-semibold text-foreground text-sm">
                          #{payout?.id?.slice(0, 8)}
                        </h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.color} flex items-center space-x-1`}>
                          <Icon name={statusConfig?.icon} size={12} strokeWidth={2} />
                          <span>{statusConfig?.label}</span>
                        </span>
                      </div>
                      <div className="flex items-center flex-wrap gap-3 text-xs text-muted-foreground mb-2">
                        <span className="flex items-center space-x-1">
                          <Icon name="User" size={12} strokeWidth={2} />
                          <span>{payout?.owner?.fullName || payout?.owner?.full_name || 'Owner'}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Icon name="Calendar" size={12} strokeWidth={2} />
                          <span>{new Date(payout?.createdAt)?.toLocaleDateString()}</span>
                        </span>
                      </div>
                      <p className="font-body font-semibold text-foreground">
                        ${parseFloat(payout?.netAmount || payout?.amount || 0)?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      {/* Fraud Detection */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Shield" size={20} strokeWidth={2} className="text-primary" />
          <span>Fraud Detection Systems</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Activity" size={16} strokeWidth={2} className="text-primary" />
              <span className="font-body text-sm font-medium">Pattern Analysis</span>
            </div>
            <p className="font-body text-xs text-muted-foreground">Monitors unusual transaction patterns</p>
          </div>
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="AlertTriangle" size={16} strokeWidth={2} className="text-primary" />
              <span className="font-body text-sm font-medium">Risk Scoring</span>
            </div>
            <p className="font-body text-xs text-muted-foreground">Automated risk assessment for transactions</p>
          </div>
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Lock" size={16} strokeWidth={2} className="text-primary" />
              <span className="font-body text-sm font-medium">Verification</span>
            </div>
            <p className="font-body text-xs text-muted-foreground">Multi-factor authentication for high-value transactions</p>
          </div>
        </div>
      </div>
      {/* Refund Modal */}
      {refundModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-elevation-3 w-full max-w-md">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold text-foreground">Process Refund</h3>
              <button onClick={() => setRefundModal(null)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} strokeWidth={2} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-muted rounded-lg p-3">
                <p className="font-body text-sm text-foreground">
                  Booking: <span className="font-semibold">#{refundModal?.bookingReference || refundModal?.id?.slice(0, 8)}</span>
                </p>
                <p className="font-body text-sm text-muted-foreground">
                  Total: ${parseFloat(refundModal?.totalAmount || 0)?.toFixed(2)}
                </p>
              </div>
              <div>
                <label className="font-body text-sm font-medium text-foreground mb-1 block">Refund Amount ($)</label>
                <input
                  type="number"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e?.target?.value)}
                  placeholder="0.00"
                  max={refundModal?.totalAmount}
                  className="w-full px-3 py-2 bg-muted border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="font-body text-sm font-medium text-foreground mb-1 block">Reason</label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e?.target?.value)}
                  placeholder="Reason for refund..."
                  rows={3}
                  className="w-full px-3 py-2 bg-muted border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
              <div className="flex items-center justify-end space-x-3">
                <Button variant="outline" onClick={() => setRefundModal(null)}>Cancel</Button>
                <Button
                  onClick={handleProcessRefund}
                  disabled={processingRefund || !refundAmount}
                  variant="danger"
                >
                  {processingRefund ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Icon name="RotateCcw" size={16} strokeWidth={2} className="mr-2" />
                  )}
                  Process Refund
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialOversight;